import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect, useState, useCallback, useMemo } from "react";
import { ArrowRight, ArrowLeft, MapPin, X, ChevronLeft, ChevronRight } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { getWedding } from "@/lib/cloudApi";

// ── Gallery data normaliser ───────────────────────────────────
// Handles: array | JSON string | object | null/undefined
// Also checks fallback keys: gallery, images, photos, album
function normaliseGallery(raw) {
  if (!raw) return [];
  let arr = raw;
  // JSON string
  if (typeof arr === "string") {
    try { arr = JSON.parse(arr); } catch { return []; }
  }
  // Plain object (not array) → values
  if (arr && !Array.isArray(arr) && typeof arr === "object") {
    arr = Object.values(arr);
  }
  if (!Array.isArray(arr)) return [];
  // Filter valid URLs only
  return arr
    .map(item => {
      if (typeof item === "string") return item.trim();
      if (item && typeof item === "object") {
        return item.url || item.src || item.image || item.photo || "";
      }
      return "";
    })
    .filter(url => url && (url.startsWith("http") || url.startsWith("/")));
}

// ── Try gallery from multiple possible keys ───────────────────
function extractGallery(story) {
  if (!story) return [];
  for (const key of ["gallery", "images", "photos", "album"]) {
    const result = normaliseGallery(story[key]);
    if (result.length > 0) return result;
  }
  return [];
}

// ── Masonry column splitter ───────────────────────────────────
function splitIntoColumns(items, cols) {
  const columns = Array.from({ length: cols }, () => []);
  items.forEach((item, i) => columns[i % cols].push({ item, index: i }));
  return columns;
}

// ── Single gallery image ──────────────────────────────────────
function GalleryImage({ src, alt, index, onClick, priority }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  if (error) return null; // hide broken images silently

  return (
    <button
      type="button"
      onClick={() => onClick(index)}
      className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C99A45] group"
      data-testid={`gallery-img-${index}`}
      aria-label={`View ${alt} photo ${index + 1}`}
    >
      {/* Skeleton shown until image loads */}
      {!loaded && (
        <div className="w-full bg-[#1a1a1a] animate-pulse" style={{ minHeight: "200px", aspectRatio: "4/5" }} />
      )}
      <img
        src={src}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={priority ? "high" : "low"}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        className={[
          "w-full h-auto block",
          "transition-opacity duration-500",
          "group-hover:opacity-90",
          loaded ? "opacity-100" : "opacity-0 absolute top-0 left-0",
        ].join(" ")}
        style={{ display: "block" }}
      />
    </button>
  );
}

// ── Lightbox ──────────────────────────────────────────────────
function Lightbox({ images, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  if (index === null || !images[index]) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
      onClick={onClose}
      data-testid="lightbox"
    >
      {/* Image */}
      <img
        src={images[index]}
        alt={`Photo ${index + 1}`}
        className="max-w-[92vw] max-h-[90vh] object-contain"
        onClick={e => e.stopPropagation()}
      />
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/70 hover:text-white p-2"
        aria-label="Close"
      >
        <X size={24} />
      </button>
      {/* Prev */}
      {index > 0 && (
        <button
          onClick={e => { e.stopPropagation(); onPrev(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2"
          aria-label="Previous"
        >
          <ChevronLeft size={36} />
        </button>
      )}
      {/* Next */}
      {index < images.length - 1 && (
        <button
          onClick={e => { e.stopPropagation(); onNext(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2"
          aria-label="Next"
        >
          <ChevronRight size={36} />
        </button>
      )}
      {/* Counter */}
      <div className="absolute bottom-4 left-0 right-0 text-center eyebrow text-[10px] text-white/50">
        {index + 1} / {images.length}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
export default function WeddingDetail() {
  const { slug } = useParams();
  const { weddings = [], siteLoading = false } = useSite() || {};
  const [story,    setStory]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lbIndex,  setLbIndex]  = useState(null);

  const fallbackStory = useMemo(() => {
    const targetSlug = String(slug || "").toLowerCase();
    return weddings.find((item) => String(item?.slug || "").toLowerCase() === targetSlug) || null;
  }, [slug, weddings]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    getWedding(slug)
      .then((data) => {
        if (cancelled) return;
        if (data) {
          setStory(data);
          return;
        }
        if (fallbackStory) {
          setStory(fallbackStory);
          return;
        }
        if (!siteLoading) setNotFound(true);
      })
      .catch(() => {
        if (cancelled) return;
        if (fallbackStory) {
          setStory(fallbackStory);
        } else if (!siteLoading) {
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fallbackStory, siteLoading, slug]);

  const gallery = extractGallery(story);

  const openLightbox  = useCallback(i => setLbIndex(i), []);
  const closeLightbox = useCallback(() => setLbIndex(null), []);
  const prevImage     = useCallback(() => setLbIndex(i => Math.max(0, i - 1)), []);
  const nextImage     = useCallback(() => setLbIndex(i => Math.min(gallery.length - 1, i + 1)), [gallery.length]);

  if (notFound) return <Navigate to="/weddings" replace />;
  if (loading || !story) {
    return (
      <div className="min-h-screen bg-[#F7F1E8] flex items-center justify-center">
        <div className="eyebrow text-[#C99A45]">Loading story...</div>
      </div>
    );
  }

  // Cover position from admin (with fallbacks)
  const coverPos = story.coverDesktopPosition || "center center";

  return (
    <div data-testid="wedding-detail-page" className="bg-[#070707] text-[#F7F1E8]">

      {/* ── HERO COVER ─────────────────────────────────────── */}
      <section className="cinematic-hero relative h-[72vh] md:h-[88vh] overflow-hidden" data-gsap="hero">
        {story.cover ? (
          <img
            src={fileUrl(story.cover)}
            alt={story.couple}
            fetchPriority="high"
            loading="eager"
            decoding="async"
            className="absolute inset-0 w-full h-full"
            data-gsap="hero-media"
            style={{ objectFit: story.coverFit || "cover", objectPosition: coverPos }}
          />
        ) : (
          <div className="absolute inset-0 bg-[#1a1a1a]" />
        )}
        <div className="hero-grain cinematic-grain" aria-hidden="true" />
        <div className="hero-overlay cinematic-overlay absolute inset-0 bg-gradient-to-b from-black/25 via-black/20 to-black/75" />
        <div className="absolute inset-0 flex items-end z-10">
          <div className="max-w-7xl mx-auto w-full px-6 md:px-10 pb-14 md:pb-22 text-[#F7F1E8]">
            <Link
              to="/weddings"
              className="eyebrow text-[10px] inline-flex items-center gap-2 text-[#F7F1E8]/70 hover:text-[#C99A45] transition"
              data-testid="back-to-weddings"
              data-gsap="hero-kicker"
            >
              <ArrowLeft size={11} /> Back to Weddings
            </Link>
            <div className="eyebrow text-[#C99A45] mt-6 text-[10px]" data-gsap="hero-kicker">{story.type}</div>
            <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl mt-2 leading-[0.92]" data-gsap="hero-title">
              {story.couple}
            </h1>
            <div className="mt-4 flex items-center gap-2 eyebrow text-[10px] text-[#F7F1E8]/70" data-gsap="hero-subtitle">
              <MapPin size={11} /> {story.location}
            </div>
          </div>
        </div>
      </section>

      {/* ── STORY TEXT ─────────────────────────────────────── */}
      <section className="py-16 md:py-28 px-6 md:px-10 bg-[#F7F1E8] text-[#151515]" data-gsap="reveal">
        <div className="max-w-3xl mx-auto text-center">
          <div className="eyebrow text-[#C99A45] text-[10px]">The Story</div>
          <div className="gold-divider mx-auto my-6" />
          <p className="font-serif-display text-xl md:text-2xl lg:text-3xl leading-[1.5] text-[#151515]/85">
            {story.fullStory || story.story}
          </p>
        </div>
      </section>

      {/* ── GALLERY ────────────────────────────────────────── */}
      <section
        className="pb-16 md:pb-24 px-4 md:px-6 bg-[#F7F1E8]"
        data-testid="story-gallery"
        data-gsap="reveal"
      >
        {gallery.length === 0 ? (
          <div className="max-w-3xl mx-auto text-center py-10">
            <p className="eyebrow text-[10px] text-[#151515]/40">No gallery images uploaded yet.</p>
          </div>
        ) : (
          <>
            {/* ── Desktop: 3-col masonry ── */}
            <div className="hidden lg:flex gap-3" aria-label="Wedding gallery" data-gsap="stagger">
              {splitIntoColumns(gallery, 3).map((col, ci) => (
                <div key={ci} className="flex-1 flex flex-col gap-3">
                  {col.map(({ item, index }) => (
                    <GalleryImage
                      key={index}
                      src={fileUrl(item)}
                      alt={story.couple}
                      index={index}
                      onClick={openLightbox}
                      priority={index < 3}
                    />
                  ))}
                </div>
              ))}
            </div>

            {/* ── Tablet: 2-col masonry ── */}
            <div className="hidden md:flex lg:hidden gap-3" aria-label="Wedding gallery" data-gsap="stagger">
              {splitIntoColumns(gallery, 2).map((col, ci) => (
                <div key={ci} className="flex-1 flex flex-col gap-3">
                  {col.map(({ item, index }) => (
                    <GalleryImage
                      key={index}
                      src={fileUrl(item)}
                      alt={story.couple}
                      index={index}
                      onClick={openLightbox}
                      priority={index < 2}
                    />
                  ))}
                </div>
              ))}
            </div>

            {/* ── Mobile: 1 column ── */}
            <div className="flex md:hidden flex-col gap-3" aria-label="Wedding gallery" data-gsap="stagger">
              {gallery.map((item, index) => (
                <GalleryImage
                  key={index}
                  src={fileUrl(item)}
                  alt={story.couple}
                  index={index}
                  onClick={openLightbox}
                  priority={index === 0}
                />
              ))}
            </div>

            <p className="mt-6 text-center eyebrow text-[10px] text-[#151515]/35">
              {gallery.length} photograph{gallery.length !== 1 ? "s" : ""}
            </p>
          </>
        )}
      </section>

      {/* ── FILM EMBED ──────────────────────────────────────── */}
      {story.videoUrl && (
        <section className="py-16 md:py-24 px-6 md:px-10 bg-[#070707]" data-testid="story-film" data-gsap="reveal">
          <div className="max-w-5xl mx-auto">
            <div className="eyebrow text-[#C99A45] text-[10px]">The Film</div>
            <h2 className="font-serif-display text-4xl md:text-6xl mt-3">Watch the Wedding Film</h2>
            <div className="mt-10 aspect-video bg-black border border-[#C99A45]/30">
              <iframe
                src={story.videoUrl}
                title={`${story.couple} Wedding Film`}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                loading="lazy"
              />
            </div>
          </div>
        </section>
      )}

      {/* ── CTA ─────────────────────────────────────────────── */}
      <section className="py-24 md:py-32 px-6 md:px-10 bg-[#F7F1E8] text-[#151515] text-center" data-gsap="reveal">
        <h2 className="font-serif-display text-4xl md:text-6xl">Loved this story?</h2>
        <p className="mt-4 text-[#151515]/65 text-sm">Let's craft yours, beautifully.</p>
        <Link to="/contact" className="btn-outline-dark mt-10 inline-flex" data-testid="story-cta">
          Enquire Now <ArrowRight size={14} />
        </Link>
      </section>

      {/* ── LIGHTBOX ─────────────────────────────────────────── */}
      {lbIndex !== null && (
        <Lightbox
          images={gallery.map(u => fileUrl(u))}
          index={lbIndex}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}
    </div>
  );
}
